import {
  LifeReadinessCategory,
  LifeReadinessSubcategory,
  LifeReadinessQuestion,
  LifeReadinessQuestionUserLevel,
  BackrsUser,
} from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

export const lifeReadinessApi = createApi({
  reducerPath: 'lifeReadinessApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getCategories: build.query<LifeReadinessCategory[], undefined>({
      query: () => 'life-survey-category',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getSubcategories: build.query<LifeReadinessSubcategory[], undefined>({
      query: () => 'life-survey-subcategory',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getQuestions: build.query<LifeReadinessQuestion[], undefined>({
      query: () => 'life-survey-question',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getQuestionUserLevels: build.query<
      LifeReadinessQuestionUserLevel[],
      BackrsUser['id'] | undefined
    >({
      query: (userId) => `life-survey/user/${userId}/question-levels`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    updateQuestionUserLevel: build.mutation<
      LifeReadinessQuestionUserLevel,
      {
        user_id: LifeReadinessQuestionUserLevel['user_id'];
        life_survey_question_id: LifeReadinessQuestionUserLevel['life_survey_question_id'];
        level: LifeReadinessQuestionUserLevel['level'];
      }
    >({
      query: (data) => {
        const { life_survey_question_id: questionId, level, user_id } = data;

        return {
          url: `life-survey/user/${user_id}/question-levels`,
          method: 'POST',
          body: {
            questionId,
            level,
          },
        };
      },
      async onQueryStarted({ user_id, life_survey_question_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedLevel } = await queryFulfilled;

          dispatch(
            lifeReadinessApi.util.updateQueryData('getQuestionUserLevels', user_id, (draft) => {
              const index = draft.findIndex(
                (item) => item.life_survey_question_id === life_survey_question_id,
              );

              if (index !== -1) {
                draft[index] = updatedLevel;

                return;
              }

              draft.push(updatedLevel);
            }),
          );
        } catch (e) {
          console.error('Failed to update local user levels cache', e);
        }
      },
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetSubcategoriesQuery,
  useGetQuestionsQuery,

  useGetQuestionUserLevelsQuery,
  useUpdateQuestionUserLevelMutation,

  usePrefetch: usePrefetchLifeReadiness,

  util: { resetApiState },
} = lifeReadinessApi;
